import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { Button, Card, CardBody, EmptyState, StatusBadge } from "@the-drop/ui";
import type { StatusBadgeVariant } from "@the-drop/ui";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  approveDispensarySettlement,
  fetchDispensarySettlementLineItems,
  fetchDispensarySettlements,
  fetchOrgSettlements,
  removeDispensarySettlementLineItem,
  submitDispensarySettlementForApproval,
  type DispensarySettlementBatchItem,
  type DispensarySettlementLineItem,
  type OrgSettlementBatchItem,
} from "../lib/dispensary-delivery-api";
import { formatCentsAsUsd } from "../lib/format-usd";
import { useDispensaryWorkspace } from "../workspace/dispensary-workspace-context";

function batchStatusLabel(status: string): string {
  switch (status) {
    case "accumulating":
      return "Accumulating";
    case "pending_approval":
      return "Awaiting approval";
    case "approved":
      return "Approved";
    case "processing":
      return "Processing payment";
    case "settled":
      return "Settled";
    case "failed":
      return "Failed";
    default:
      return status;
  }
}

function batchStatusVariant(status: string): StatusBadgeVariant {
  switch (status) {
    case "accumulating":
      return "pending";
    case "pending_approval":
      return "warning";
    case "approved":
      return "active";
    case "processing":
      return "pending";
    case "settled":
      return "completed";
    case "failed":
      return "error";
    default:
      return "neutral";
  }
}

function formatPeriod(startIso: string, endIso: string): string {
  const d0 = new Date(startIso);
  const d1 = new Date(endIso);
  return `${d0.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} – ${d1.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
}

export function InvoicesScreen() {
  const { dispensaryId, orgId, allLocations, getToken } = useDispensaryWorkspace();
  const aggregate = allLocations && orgId != null;
  const [tab, setTab] = useState<"open" | "past">("open");
  const [batches, setBatches] = useState<DispensarySettlementBatchItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);
  const [actionKind, setActionKind] = useState<"approve" | "submit" | null>(null);

  const apiTab = tab === "open" ? "pending" : "history";

  const load = useCallback(async () => {
    if (!dispensaryId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetchDispensarySettlements(getToken, dispensaryId, apiTab);
      setBatches(r.batches);
    } catch {
      setError("Could not load invoices.");
    } finally {
      setLoading(false);
    }
  }, [dispensaryId, getToken, apiTab]);

  useEffect(() => {
    void load();
  }, [load]);

  const pendingApproval = useMemo(
    () => batches.filter((b) => b.status === "pending_approval"),
    [batches],
  );
  const restOpen = useMemo(
    () => batches.filter((b) => b.status !== "pending_approval"),
    [batches],
  );

  async function onApprove(batchId: string) {
    if (!dispensaryId) return;
    setActionId(batchId);
    setActionKind("approve");
    setError(null);
    try {
      await approveDispensarySettlement(getToken, dispensaryId, batchId);
      await load();
    } catch {
      setError("Approve failed. The batch must be awaiting approval.");
    } finally {
      setActionId(null);
      setActionKind(null);
    }
  }

  async function onSubmitForApproval(batchId: string) {
    if (!dispensaryId) return;
    setActionId(batchId);
    setActionKind("submit");
    setError(null);
    try {
      await submitDispensarySettlementForApproval(getToken, dispensaryId, batchId);
      await load();
    } catch (e) {
      setError(
        e instanceof Error && e.message === "submit_settlement_empty_batch"
          ? "Add at least one delivery with fees before submitting, or wait for new deliveries to accumulate."
          : "Submit for approval failed. The batch must be accumulating.",
      );
    } finally {
      setActionId(null);
      setActionKind(null);
    }
  }

  // "All locations": aggregated read-only board. Approve/submit are per-store,
  // so a manager picks a single location to act.
  if (aggregate) {
    return <OrgSettlementsBoard getToken={getToken} orgId={orgId!} />;
  }

  if (!dispensaryId) {
    return (
      <p className="text-sm text-amber-200/90">Set dispensary scope to view invoices.</p>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-[#111111]">Invoices</h1>
        <p className="mt-1 max-w-2xl text-sm text-gray-600">
          Settlement batches by transporter and billing period. While a batch is still accumulating, choose which
          deliveries to include; removing one defers it to a later invoice. Submit for approval when ready, then
          approve to trigger payouts.
        </p>
      </div>

      <div className="flex gap-2 border-b border-border pb-2">
        {(
          [
            { id: "open" as const, label: "Current & open" },
            { id: "past" as const, label: "Past invoices" },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.id
                ? "bg-[#111111] text-white"
                : "text-gray-500 hover:bg-[#F5F5F3] hover:text-[#111111]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? <p className="text-sm text-gray-500">Loading…</p> : null}

      {tab === "open" && !loading && pendingApproval.length > 0 ? (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-[#111111]">Needs your approval</h2>
          <ul className="space-y-3">
            {pendingApproval.map((b) => (
              <InvoiceCard
                key={b.id}
                batch={b}
                getToken={getToken}
                dispensaryId={dispensaryId}
                onBatchUpdated={load}
                primaryAction={
                  <Button
                    type="button"
                    className="min-w-[140px]"
                    disabled={actionId === b.id}
                    onClick={() => void onApprove(b.id)}
                  >
                    {actionId === b.id && actionKind === "approve" ? "Approving…" : "Approve invoice"}
                  </Button>
                }
              />
            ))}
          </ul>
        </section>
      ) : null}

      {tab === "open" && !loading && pendingApproval.length === 0 && restOpen.length === 0 ? (
        <EmptyState
          icon={<DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />}
          title="No open invoices"
          subtitle="When delivery fees accumulate into a batch, they will appear here for review."
        />
      ) : null}

      {tab === "open" && restOpen.length > 0 ? (
        <section className="space-y-3">
          {pendingApproval.length > 0 ? (
            <h2 className="text-sm font-semibold text-[#111111]">In progress</h2>
          ) : null}
          <ul className="space-y-3">
            {restOpen.map((b) => (
              <InvoiceCard
                key={b.id}
                batch={b}
                getToken={getToken}
                dispensaryId={dispensaryId}
                onBatchUpdated={load}
                primaryAction={
                  b.status === "accumulating" ? (
                    <Button
                      type="button"
                      variant="outlined"
                      className="min-w-[160px]"
                      disabled={actionId === b.id}
                      onClick={() => void onSubmitForApproval(b.id)}
                    >
                      {actionId === b.id && actionKind === "submit" ? "Submitting…" : "Submit for approval"}
                    </Button>
                  ) : null
                }
              />
            ))}
          </ul>
        </section>
      ) : null}

      {tab === "past" ? (
        <>
          {!loading && batches.length === 0 ? (
            <EmptyState
              icon={<DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />}
              title="No past invoices"
              subtitle="Approved and settled batches will appear here."
            />
          ) : (
            <ul className="space-y-3">
              {batches.map((b) => (
                <InvoiceCard
                  key={b.id}
                  batch={b}
                  getToken={getToken}
                  dispensaryId={dispensaryId}
                  onBatchUpdated={load}
                />
              ))}
            </ul>
          )}
        </>
      ) : null}
    </div>
  );
}

function InvoiceCard({
  batch: b,
  primaryAction,
  getToken,
  dispensaryId,
  onBatchUpdated,
}: {
  batch: DispensarySettlementBatchItem;
  primaryAction?: ReactNode;
  getToken?: () => Promise<string | null>;
  dispensaryId?: string;
  onBatchUpdated?: () => void;
}) {
  return (
    <li>
      <Card className="border border-border shadow-sm">
        <CardBody className="p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge variant={batchStatusVariant(b.status)}>{batchStatusLabel(b.status)}</StatusBadge>
                <span className="font-mono text-xs text-gray-500">{b.id.slice(0, 8)}…</span>
              </div>
              <p className="text-base font-semibold text-[#111111]">{b.transporterName}</p>
              <p className="text-sm text-gray-600">{formatPeriod(b.periodStart, b.periodEnd)}</p>
              <p className="text-xs text-gray-500">
                {b.successfulDeliveryCount}/{b.deliveryCount} successful deliveries
              </p>
              {b.approvalRequestedAt ? (
                <p className="text-xs text-gray-500">
                  Approval requested{" "}
                  {new Date(b.approvalRequestedAt).toLocaleString(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </p>
              ) : null}
              {b.approvedAt ? (
                <p className="text-xs text-gray-500">
                  Approved{" "}
                  {new Date(b.approvedAt).toLocaleString(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </p>
              ) : null}
            </div>
            <div className="text-right text-sm">
              <p className="text-gray-600">Transporter fees</p>
              <p className="font-semibold tabular-nums text-[#111111]">{formatCentsAsUsd(b.transporterFeeTotalCents)}</p>
              <p className="mt-2 text-gray-600">Platform (dispensary)</p>
              <p className="font-medium tabular-nums text-[#111111]">
                {formatCentsAsUsd(b.dispensaryPlatformFeeTotalCents)}
              </p>
              <p className="mt-2 text-gray-600">Platform (transporter)</p>
              <p className="font-medium tabular-nums text-[#111111]">
                {formatCentsAsUsd(b.transporterPlatformFeeTotalCents)}
              </p>
            </div>
          </div>
          {b.status === "accumulating" && getToken && dispensaryId && onBatchUpdated ? (
            <AccumulatingDeliveriesTable
              batchId={b.id}
              dispensaryId={dispensaryId}
              getToken={getToken}
              onChanged={onBatchUpdated}
            />
          ) : null}
          {primaryAction ? <div className="mt-4 flex justify-end border-t border-border pt-4">{primaryAction}</div> : null}
        </CardBody>
      </Card>
    </li>
  );
}

function AccumulatingDeliveriesTable(props: {
  batchId: string;
  dispensaryId: string;
  getToken: () => Promise<string | null>;
  onChanged: () => void;
}) {
  const { batchId, dispensaryId, getToken, onChanged } = props;
  const [lineItems, setLineItems] = useState<DispensarySettlementLineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchDispensarySettlementLineItems(getToken, dispensaryId, batchId);
      setLineItems(r.lineItems);
    } catch {
      setError("Could not load deliveries for this invoice.");
      setLineItems([]);
    } finally {
      setLoading(false);
    }
  }, [batchId, dispensaryId, getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onRemove(lineItemId: string) {
    setRemovingId(lineItemId);
    setError(null);
    try {
      await removeDispensarySettlementLineItem(getToken, dispensaryId, batchId, lineItemId);
      await load();
      onChanged();
    } catch {
      setError("Could not remove delivery from this invoice.");
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <div className="mt-4 border-t border-border pt-4">
      <h3 className="text-sm font-semibold text-[#111111]">Deliveries in this invoice</h3>
      <p className="mt-1 text-xs text-gray-500">
        Remove a delivery to pay it on a later billing period instead of this batch.
      </p>
      {error ? (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? (
        <p className="mt-2 text-sm text-gray-500">Loading deliveries…</p>
      ) : lineItems.length === 0 ? (
        <p className="mt-2 text-sm text-gray-500">No deliveries in this batch yet.</p>
      ) : (
        <div className="mt-3 overflow-x-auto rounded-lg border border-border">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-[#F5F5F3]">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-700">Customer</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">Delivered</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700">Transporter fee</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700">Platform (you)</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700">Platform (trans.)</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-white">
              {lineItems.map((row) => (
                <tr key={row.id}>
                  <td className="whitespace-nowrap px-3 py-2 text-[#111111]">{row.customerName}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                    {new Date(row.deliveredAt).toLocaleString(undefined, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-800">
                    {formatCentsAsUsd(row.transporterFeeCents)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-800">
                    {formatCentsAsUsd(row.dispensaryPlatformFeeCents)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-800">
                    {formatCentsAsUsd(row.transporterPlatformFeeCents)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    <button
                      type="button"
                      className="text-sm font-medium text-amber-800 underline-offset-2 hover:underline disabled:opacity-50"
                      disabled={removingId === row.id}
                      onClick={() => void onRemove(row.id)}
                    >
                      {removingId === row.id ? "Removing…" : "Remove"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * "All locations" invoices — aggregated settlement batches across the org's
 * stores, read-only. Each batch is tagged with its location. Approve / submit
 * for approval are per-store, so a manager picks a single location to act.
 */
function OrgSettlementsBoard({
  getToken,
  orgId,
}: {
  getToken: () => Promise<string | null>;
  orgId: string;
}) {
  const [tab, setTab] = useState<"open" | "past">("open");
  const [batches, setBatches] = useState<OrgSettlementBatchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiTab = tab === "open" ? "pending" : "history";

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchOrgSettlements(getToken, orgId, apiTab);
      setBatches(r.batches);
    } catch {
      setError("Could not load invoices.");
    } finally {
      setLoading(false);
    }
  }, [getToken, orgId, apiTab]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-[#111111]">Invoices · All locations</h1>
        <p className="mt-1 text-sm text-[#777777]">
          Settlement batches across your stores. Approve / submit for approval is per-store — pick a
          single location to act on a batch.
        </p>
      </div>

      <div className="flex gap-2">
        {(["open", "past"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`rounded-md px-3 py-1.5 text-sm ${
              tab === t ? "bg-[#111111] text-white" : "border border-gray-200 text-gray-700"
            }`}
          >
            {t === "open" ? "Open" : "Past"}
          </button>
        ))}
      </div>

      {error ? <p className="text-sm text-red-300">{error}</p> : null}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : batches.length === 0 ? (
        <EmptyState
          icon={<DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />}
          title="No invoices"
          subtitle="No settlement batches in this tab across your locations."
        />
      ) : (
        <div className="space-y-3">
          {batches.map((b) => (
            <Card key={b.id}>
              <CardBody>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      {b.locationLabel?.trim() ? b.locationLabel : b.locationName}
                    </span>
                    <StatusBadge variant={batchStatusVariant(b.status)}>
                      {batchStatusLabel(b.status)}
                    </StatusBadge>
                  </div>
                  <span className="text-sm font-semibold text-[#111111]">
                    {formatCentsAsUsd(b.transporterFeeTotalCents)}
                  </span>
                </div>
                <p className="mt-2 text-sm text-gray-700">{b.transporterName}</p>
                <p className="mt-1 text-xs text-gray-500">
                  {formatPeriod(b.periodStart, b.periodEnd)} · {b.successfulDeliveryCount}/
                  {b.deliveryCount} deliveries
                </p>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
